import { Badge } from '@/components/ui/badge';
import { Gem, Coins, Check, LockIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { getCardImageUrl } from '@/utils/deck-utils';
import type { CardDeck } from '@shared/schema';

interface CompactDeckCardProps {
  readonly deck: CardDeck;
  readonly onClick: () => void;
  readonly isOwned?: boolean;
  readonly canAccess?: boolean;
}

export function CompactDeckCard({ 
  deck, 
  onClick, 
  isOwned, 
  canAccess = true 
}: Readonly<CompactDeckCardProps>) {
  // Get background gradient based on deck type
  const getDeckTypeGradient = (deckType: string) => {
    switch (deckType.toLowerCase()) {
      case "standard":
        return "bg-gradient-to-b from-gray-900/60 to-black/80";
      case "special":
        return "bg-gradient-to-b from-blue-900/60 to-black/80";
      case "limited":
        return "bg-gradient-to-b from-purple-900/60 to-black/80";
      default:
        return "bg-gradient-to-b from-gray-900/60 to-black/80";
    }
  };
  
  // Randomly select a king for preview
  const kings = ["A13", "B13", "C13", "D13"] as const;
  const randomKing = kings[Math.floor(Math.random() * kings.length)];
  
  return (
    <div 
      className={cn(
        "overflow-hidden rounded-lg bg-black/80 hover:bg-black/90 transition-all cursor-pointer h-full flex flex-col w-full text-left",
        !canAccess && !isOwned && "opacity-75"
      )}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onClick();
        }
      }}
      role="button"
      tabIndex={0}
    >
      {/* Card Preview */}
      <div className={cn(
        "relative flex-grow", 
        getDeckTypeGradient(deck.deckType),
        !canAccess && !isOwned && "grayscale-[30%]"
      )}>
        
        
        {!canAccess && !isOwned && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40 z-10">
            <div className="bg-black/70 p-2 rounded-full">
              <LockIcon className="h-6 w-6 text-white/70" />
            </div>
          </div>
        )}
        
        <div className="aspect-square w-full relative flex justify-center items-center">
          <motion.div className="absolute transform translate-x-3">
            <img
              src={getCardImageUrl(deck.deckUrl, "back")}
              alt="Card Back"
              className="w-32 h-64 object-contain"
            />
          </motion.div>
          <motion.div className="absolute transform -translate-x-3">
            <img
              src={getCardImageUrl(deck.deckUrl, randomKing)}
              alt={deck.name}
              className="w-32 h-64 object-contain"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).src = getCardImageUrl(deck.deckUrl, "back");
              }}
            />
          </motion.div>
        </div>
      </div>
      
      {/* Deck Info */}
      <div className="p-3 flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <h3 className="font-bold text-base text-white line-clamp-1 flex-1">
            {deck.name}
          </h3>
          
          {parseFloat(deck.price) > 0 && (
            <div className="text-amber-400 font-medium flex items-center text-sm flex-shrink-0">
              {Math.floor(parseFloat(deck.price))}
              {deck.currencyType === 'premium' ? (
                <Gem className="h-3 w-3 ml-0.5 text-purple-400" />
              ) : (
                <Coins className="h-3 w-3 ml-0.5" />
              )}
            </div>
          )}
        </div>
        
        <div className="flex gap-1 flex-wrap">
          {isOwned && (
            <Badge variant="outline" className="text-xs bg-black/30 border-gray-600 text-gray-300 flex items-center gap-1">
              <Check className="h-3 w-3" />
              <span>Owned</span>
            </Badge>
          )}
          
          {parseFloat(deck.price) === 0 && (
            <Badge variant="outline" className="text-xs bg-black/30 border-gray-600 text-gray-300">
              Free
            </Badge>
          )}

          {deck.managerMode && (
            <Badge variant="outline" className="text-xs bg-black/30 border-gray-600 text-purple-400">
              Manager
            </Badge>
          )}
        </div>
      </div>
      
    </div>
  );
}